// Pure helpers for database password management (Database → Users and the
// rotation setting on the Databases page). No React, no network: tested in
// dbVaultModel.test.js.

const MIN = 60e3;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

// A managed password belongs to a role on one database server, so every
// action and every lookup is keyed by (hostId, role).
export const accountKey = (hostId, role) => `${hostId}|${String(role ?? "").trim()}`;
const hostRoleKey = (host, role) => `${String(host ?? "").trim()}|${String(role ?? "").trim()}`;

// "5 min" / "2 h" / "3 days": a duration in its largest sensible unit.
export const shortDuration = (ms) => {
  const abs = Math.abs(ms);
  if (abs < HOUR) return `${Math.max(1, Math.round(abs / MIN))} min`;
  if (abs < 2 * DAY) return `${Math.round(abs / HOUR)} h`;
  const days = Math.round(abs / DAY);
  return `${days} day${days === 1 ? "" : "s"}`;
};

const timeOf = (iso) => {
  if (!iso) return NaN;
  return new Date(iso).getTime();
};

// "2 h ago", "just now"; "" when there is no time.
export const agoText = (iso, now = Date.now()) => {
  const t = timeOf(iso);
  if (Number.isNaN(t)) return "";
  const ms = now - t;
  if (ms < MIN) return "just now";
  return `${shortDuration(ms)} ago`;
};

// "in 22 h", "now" once due; "" when there is no time.
export const inText = (iso, now = Date.now()) => {
  const t = timeOf(iso);
  if (Number.isNaN(t)) return "";
  const ms = t - now;
  if (ms < MIN) return "now";
  return `in ${shortDuration(ms)}`;
};

// "rotated 2 h ago · next in 22 h" for a managed (active) password.
export const managedSummary = (account, now = Date.now()) => {
  if (!account) return "";
  const parts = [];
  const ago = agoText(account.rotatedAt, now);
  if (ago) parts.push(`rotated ${ago}`);
  const next = inText(account.nextDueAt, now);
  if (next) parts.push(next === "now" ? "next due now" : `next ${next}`);
  return parts.join(" · ");
};

export const isManaged = (account) => !!account && account.enabled === true;

// What the Password column shows for a row: { kind, label, tone }.
// kind is none | pending | rotating | active | failed.
export const passwordState = (account) => {
  if (!isManaged(account)) return { kind: "none", label: "Not managed", tone: "neutral" };
  switch (String(account.status || "").toLowerCase()) {
    case "pending":
      return { kind: "pending", label: "Taking over…", tone: "info" };
    case "rotating":
      return { kind: "rotating", label: "Rotating…", tone: "info" };
    case "failed":
      return { kind: "failed", label: "Rotation failed", tone: "danger" };
    default:
      return { kind: "active", label: "Managed", tone: "success" };
  }
};

// Tooltip for a failed rotation: the agent's detail and the failure count.
export const failureTooltip = (account) => {
  const detail = String(account?.detail || "").trim() || "The agent didn't report a reason.";
  const n = Number(account?.failures) || 0;
  return n > 0 ? `${detail} Failed ${n} time${n === 1 ? "" : "s"} in a row.` : detail;
};

export const isInFlight = (account) => {
  const kind = passwordState(account).kind;
  return kind === "pending" || kind === "rotating";
};

// True while any managed password is being taken over or rotated, which is
// when Database → Users keeps polling the list.
export const anyInFlight = (accounts) => (accounts || []).some(isInFlight);

// Index a password/list response for joining with the users list.
export const indexAccounts = (accounts) => {
  const byKey = new Map();
  const byHostRole = new Map();
  (accounts || []).forEach((a) => {
    if (a == null) return;
    byKey.set(accountKey(a.hostId, a.role), a);
    if (a.host) byHostRole.set(hostRoleKey(a.host, a.role), a);
  });
  return { byKey, byHostRole };
};

// The db_hosts id for a users-list row: its own host_id when the list sends
// one, else the id the host's address maps to (listDbHosts), else null.
export const hostIdOf = (row, hostIdByAddress = {}) => {
  const own = row?.host_id ?? row?.hostId;
  if (own != null && Number(own) > 0) return Number(own);
  const mapped = hostIdByAddress[String(row?.host ?? "").trim()];
  return mapped != null && Number(mapped) > 0 ? Number(mapped) : null;
};

// The users list names the database role "username" (role is the app's
// Admin/User label).
export const dbRoleOf = (row) => String(row?.user_name ?? row?.username ?? "").trim();

// Join one users-list row with the managed state: { hostId, role, account }.
// A row whose host the console can't place still joins by host address.
export const joinRow = (row, index, hostIdByAddress = {}) => {
  const role = dbRoleOf(row);
  let hostId = hostIdOf(row, hostIdByAddress);
  let account = hostId != null ? index.byKey.get(accountKey(hostId, role)) : undefined;
  if (!account) {
    account = index.byHostRole.get(hostRoleKey(row?.host, role));
    if (account && hostId == null) hostId = Number(account.hostId) || null;
  }
  return { hostId, role, account: account || null };
};

/* ------------------------------------------------ rotation interval setting */

export const ROTATION_MIN_HOURS = 1;
export const ROTATION_MAX_HOURS = 8760;
export const ROTATION_DEFAULT_HOURS = 24;

export const ROTATION_PRESETS = [
  { label: "1 h", hours: 1 },
  { label: "1 day", hours: 24 },
  { label: "1 week", hours: 168 },
  { label: "30 days", hours: 720 },
];

// "" when valid, else the message to show under the field.
export const rotationHoursError = (value) => {
  if (value === null || value === undefined || value === "") return "Enter a number of hours.";
  const n = Number(value);
  if (!Number.isInteger(n)) return "Use a whole number of hours.";
  if (n < ROTATION_MIN_HOURS || n > ROTATION_MAX_HOURS) {
    return `Use ${ROTATION_MIN_HOURS} to ${ROTATION_MAX_HOURS} hours.`;
  }
  return "";
};

// "every 168 hours" → "1 week" style hint beside the field; "" when it isn't a round unit.
export const hoursHint = (value) => {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return "";
  if (n % 168 === 0) return `${n / 168} week${n === 168 ? "" : "s"}`;
  if (n % 24 === 0) return `${n / 24} day${n === 24 ? "" : "s"}`;
  return "";
};

// "Last changed": the later of the settings' updatedAt, with who made it.
export const lastChanged = (...settings) => {
  let best = null;
  settings.forEach((s) => {
    const t = timeOf(s?.updatedAt);
    if (Number.isNaN(t)) return;
    if (!best || t > best.t) best = { t, at: s.updatedAt, by: s.updatedBy ?? null };
  });
  return best ? { at: best.at, by: best.by } : null;
};

// Who changed it: a console user's name or email, else "User #id".
export const userLabel = (id, users = []) => {
  if (id === null || id === undefined || id === "") return "";
  const u = (users || []).find((x) => String(x?.id) === String(id));
  if (u) return u.name || u.email || `User #${id}`;
  return `User #${id}`;
};

/* ------------------------------------------------------------ reveal dialog */

export const REVEAL_POLL_MS = 2000;
export const REVEAL_TIMEOUT_MS = 5 * 60e3;
export const REVEAL_DEFAULT_SHOW_SECONDS = 60;

// "0:59"
export const countdownText = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

// What a failed reveal call means for the dialog.
export const revealFailure = (status) => {
  if (status === 404) return { kind: "unavailable", text: "Reveal isn't available on this server yet." };
  if (status === 403) return { kind: "error", text: "Only administrators can reveal passwords." };
  return { kind: "error", text: "Couldn't reach AuthNull. Try again." };
};

// Message for a failed manage or rotate call.
export const actionFailureText = (status, op) => {
  if (status === 403) return "Only administrators can change database passwords.";
  if (status === 404) {
    return op === "rotate"
      ? "This password isn't managed. Turn management on first."
      : "This database host isn't in this tenant.";
  }
  if (status === 400) return "AuthNull couldn't use this role name.";
  return "Couldn't queue the change. Try again.";
};
